/**
 * AddEditEventSeriesDialog
 * Dialog for creating/editing event series.
 */

import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useCreateEventSeries, useUpdateEventSeries, useEcosystemOrganizations } from '@/hooks/useEcosystemData';
import { EVENT_FREQUENCY_LABELS, type EventFrequency } from '@/types/ecosystem';

interface AddEditEventSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  series?: any | null;
  defaultOrganizerId?: string;
}

export function AddEditEventSeriesDialog({
  open,
  onOpenChange,
  series,
  defaultOrganizerId,
}: AddEditEventSeriesDialogProps) {
  const isEditMode = !!series;

  const [seriesName, setSeriesName] = useState('');
  const [organizerProviderId, setOrganizerProviderId] = useState('');
  const [frequency, setFrequency] = useState<string>('annual');
  const [defaultAudience, setDefaultAudience] = useState('');
  const [defaultTracks, setDefaultTracks] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [stableKey, setStableKey] = useState('');

  const createSeries = useCreateEventSeries();
  const updateSeries = useUpdateEventSeries();
  const { data: orgs } = useEcosystemOrganizations();

  useEffect(() => {
    if (open && series) {
      setSeriesName(series.series_name || '');
      setOrganizerProviderId(series.organizer_provider_id || '');
      setFrequency(series.frequency || 'annual');
      setDefaultAudience(series.default_audience?.join(', ') || '');
      setDefaultTracks(series.default_tracks?.join(', ') || '');
      setWebsiteUrl(series.website_url || '');
      setNotes(series.notes || '');
      setStableKey(series.stable_key || '');
    } else if (open) {
      setSeriesName('');
      setOrganizerProviderId(defaultOrganizerId || '');
      setFrequency('annual');
      setDefaultAudience('');
      setDefaultTracks('');
      setWebsiteUrl('');
      setNotes('');
      setStableKey('');
    }
  }, [open, series, defaultOrganizerId]);

  const handleSubmit = async () => {
    if (!seriesName.trim()) {
      toast.error('Series name is required');
      return;
    }
    if (!organizerProviderId) {
      toast.error('Organizer is required');
      return;
    }

    try {
      if (isEditMode) {
        await updateSeries.mutateAsync({
          data: {
            seriesId: series.id,
            series_name: seriesName.trim(),
            frequency,
            default_audience: defaultAudience,
            default_tracks: defaultTracks,
            website_url: websiteUrl.trim() || null,
            notes: notes.trim() || null,
          },
        });
        toast.success('Event series updated');
      } else {
        await createSeries.mutateAsync({
          data: {
            series_name: seriesName.trim(),
            organizer_provider_id: organizerProviderId,
            frequency,
            default_audience: defaultAudience,
            default_tracks: defaultTracks,
            website_url: websiteUrl.trim() || null,
            notes: notes.trim() || null,
            stable_key: stableKey.trim() || null,
          },
        });
        toast.success('Event series created');
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save event series');
    }
  };

  const isSubmitting = createSeries.isPending || updateSeries.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Event Series' : 'Add Event Series'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update event series details.' : 'Create a new recurring event brand (e.g., VISION, AAPEX).'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="seriesName">Series Name *</Label>
            <Input id="seriesName" value={seriesName} onChange={(e) => setSeriesName(e.target.value)} placeholder="e.g., VISION" />
          </div>

          <div className="grid gap-2">
            <Label>Organizer *</Label>
            <Select value={organizerProviderId} onValueChange={setOrganizerProviderId}>
              <SelectTrigger><SelectValue placeholder="Select organizer" /></SelectTrigger>
              <SelectContent>
                {(orgs || []).map((o: any) => (
                  <SelectItem key={o.id} value={o.id}>{o.display_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.entries(EVENT_FREQUENCY_LABELS) as [EventFrequency, string][]).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="audience">Default Audience</Label>
              <Input id="audience" value={defaultAudience} onChange={(e) => setDefaultAudience(e.target.value)} placeholder="technician, advisor" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="tracks">Default Tracks</Label>
              <Input id="tracks" value={defaultTracks} onChange={(e) => setDefaultTracks(e.target.value)} placeholder="diagnostics, electrical" />
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="websiteUrl">Website URL</Label>
            <Input id="websiteUrl" type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          {!isEditMode && (
            <div className="grid gap-2">
              <Label htmlFor="stableKey">Stable Key</Label>
              <Input id="stableKey" value={stableKey} onChange={(e) => setStableKey(e.target.value)} placeholder="Auto-generated" className="font-mono text-sm" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Series'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
